import { Box } from "@mui/material";
import { Typography } from "@platform/design-system";

import { CARD_SX, TIER_ORDER } from "./constants";
import { fmtCurrencyExact, fmtNumber } from "./formatters";
import { DonutChart } from "./DonutChart";
import { CardTitle, HelpIcon, LegendKey } from "./primitives";

type PendingOutcomesCardProps = {
  tier1Premium: number;
  tier2Premium: number;
  nonStrategicPremium: number;
  tier1Count: number;
  tier2Count: number;
  nonStrategicCount: number;
  pendingRfpCount: number;
  showGroupCounts: boolean;
};

const PENDING_OUTCOMES_DESCRIPTION =
  "Shows groups that have a quote but no final carrier choice yet. Premium is split by the current carrier tier.";

const PENDING_OUTCOMES_COUNT_DESCRIPTION =
  "Shows groups that have a quote but no final carrier choice yet. Groups are counted once by current carrier tier.";

function PendingOutcomesCard({
  tier1Premium,
  tier2Premium,
  nonStrategicPremium,
  tier1Count,
  tier2Count,
  nonStrategicCount,
  pendingRfpCount,
  showGroupCounts,
}: PendingOutcomesCardProps): React.JSX.Element {
  const formattedPendingRfpCount = fmtNumber(pendingRfpCount);
  const tierValues = showGroupCounts
    ? {
        tier1: tier1Count,
        tier2: tier2Count,
        nonStrategic: nonStrategicCount,
      }
    : {
        tier1: tier1Premium,
        tier2: tier2Premium,
        nonStrategic: nonStrategicPremium,
      };
  const valueFormatter = showGroupCounts ? fmtNumber : fmtCurrencyExact;

  return (
    <Box
      data-testid="pending-outcomes-card"
      sx={{ ...CARD_SX, width: 400, flexShrink: 0 }}
    >
      <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
        <CardTitle>Pending Outcomes</CardTitle>
        <HelpIcon
          label="Pending Outcomes"
          description={
            showGroupCounts
              ? PENDING_OUTCOMES_COUNT_DESCRIPTION
              : PENDING_OUTCOMES_DESCRIPTION
          }
          ariaLabel="Pending Outcomes card definition"
        />
      </Box>
      <Box
        sx={{
          mt: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography>
            <strong>Groups Pending Decision</strong>
          </Typography>
          <Typography
            aria-label="Groups pending decision count"
            variant="body-large"
            color="textSecondary"
          >
            {formattedPendingRfpCount}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          <DonutChart
            tier1={tierValues.tier1}
            tier2={tierValues.tier2}
            nonStrategic={tierValues.nonStrategic}
            centerLabel={formattedPendingRfpCount}
            centerSublabel="Groups"
            valueLabel={showGroupCounts ? "Groups" : "Exact premium"}
            valueFormatter={valueFormatter}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {TIER_ORDER.map((tier) => (
              <LegendKey
                key={tier}
                tier={tier}
                value={valueFormatter(
                  tier === "Tier 1"
                    ? tierValues.tier1
                    : tier === "Tier 2"
                      ? tierValues.tier2
                      : tierValues.nonStrategic,
                )}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export { PendingOutcomesCard };
